import {
	getConfigStorageItem,
	removeConfigStorageItem,
	setConfigStorageItem,
} from '../hooks/useConfigStorage';
import { getCookieCustom } from './cookies';

export type SmartSearchMatchType =
	| 'startsWith'
	| 'includes'
	| 'equals'
	| 'regex';

// Works against any existing XTable view: the matched rule navigates to its
// route and hands the search text off via redux (smartSearchSlice); the
// target grid then applies it through its normal filter pipeline
// (see XTable/hooks/useSmartSearchFilter).
export type SmartSearchRule = {
	id: string;
	name: string;
	priority: number;
	matchType: SmartSearchMatchType;
	matchValue: string;
	route: string;
	filterField: string;
	filterOperator?: 'contains' | 'equals' | 'startsWith' | 'endsWith';
	isBuiltIn: boolean;
};

export type SmartSearchHistoryEntry = {
	text: string;
	ruleId: string | null;
	route: string | null;
	timestamp: number;
};

export const SMART_SEARCH_CUSTOM_RULES_KEY = 'smartSearch.customRules';
export const SMART_SEARCH_RECENT_KEY = 'smartSearch.recentSearches';
export const SMART_SEARCH_HISTORY_LIMIT = 10;

// Custom rules persist to the backend as a ClientConfiguration item under this
// Parameter (Value = JSON array of rules). Mirrors how column/field presets
// are stored. LocalStorage stays the fast local cache the matcher reads.
export const SMART_SEARCH_CONFIG_PARAMETER = 'smartSearchRules';

const FILTER_OPERATORS = ['contains', 'equals', 'startsWith', 'endsWith'];

export const SMART_SEARCH_FILTER_OPERATORS = FILTER_OPERATORS;

// Pages a smart search rule can target (existing grid views only).
// entityKey resolves the label via the `entities.*` translations.
export const SMART_SEARCH_TARGET_PAGES: {
	route: string;
	entityKey: string;
}[] = [
	{ route: '/results', entityKey: 'results' },
	{ route: '/customers', entityKey: 'customers' },
	{ route: '/orders', entityKey: 'orders' },
	{ route: '/drivers', entityKey: 'drivers' },
	{ route: '/vehicles', entityKey: 'vehicles' },
	{ route: '/products', entityKey: 'products' },
	{ route: '/contracts', entityKey: 'contracts' },
	{ route: '/cards', entityKey: 'cards' },
	{ route: '/warehouse', entityKey: 'warehouse' },
	{ route: '/destPoint', entityKey: 'destPoint' },
	{ route: '/spy', entityKey: 'spy' },
];

// Built-in default rules: one per grid, matching any non-empty text against
// that grid's primary lookup field. Their deeply negative priorities keep them
// below every user-defined rule (the dialog defaults to priority 50), so the
// dropdown lists user rule matches first and these defaults after — Enter
// fires the first user match, or the Results default when nothing else maps.
const DEFAULT_RULE_BASE_PRIORITY = -1000;

const DEFAULT_RULE_TARGETS: { route: string; filterField: string }[] = [
	{ route: '/results', filterField: 'ExternalCode' },
	{ route: '/customers', filterField: 'Name' },
	{ route: '/orders', filterField: 'Number' },
	{ route: '/drivers', filterField: 'FullName' },
	{ route: '/vehicles', filterField: 'Number' },
	{ route: '/products', filterField: 'Name' },
	{ route: '/contracts', filterField: 'Name' },
	{ route: '/destPoint', filterField: 'Name' },
	{ route: '/cards', filterField: 'HardwareCode' },
	{ route: '/warehouse', filterField: 'DestPoint' },
	{ route: '/spy', filterField: 'ScalesName' },
];

export const builtInSmartSearchRules: SmartSearchRule[] =
	DEFAULT_RULE_TARGETS.map((target, index) => ({
		id: `default${target.route.replace(/\//g, '-')}`,
		name: target.filterField,
		// Descending so the defaults keep the order defined above.
		priority: DEFAULT_RULE_BASE_PRIORITY - index,
		matchType: 'regex',
		matchValue: '^.+$',
		route: target.route,
		filterField: target.filterField,
		// No explicit operator: the target grid picks one from the field's
		// structure type (string → contains, numeric → equals).
		isBuiltIn: true,
	}));

const MATCH_TYPES: SmartSearchMatchType[] = [
	'startsWith',
	'includes',
	'equals',
	'regex',
];

const isSafeRegex = (pattern: string) => {
	try {
		// eslint-disable-next-line no-new
		new RegExp(pattern);
		return true;
	} catch {
		return false;
	}
};

// Rules are plain data only (match type + value + target route); nothing
// loaded from localStorage is ever executed.
export const isValidSmartSearchRule = (
	value: unknown,
): value is SmartSearchRule => {
	if (!value || typeof value !== 'object' || Array.isArray(value)) {
		return false;
	}
	const rule = value as Record<string, unknown>;
	if (typeof rule.id !== 'string' || !rule.id.trim()) return false;
	if (typeof rule.name !== 'string' || !rule.name.trim()) return false;
	if (typeof rule.priority !== 'number' || !Number.isFinite(rule.priority)) {
		return false;
	}
	if (!MATCH_TYPES.includes(rule.matchType as SmartSearchMatchType)) {
		return false;
	}
	if (typeof rule.matchValue !== 'string' || !rule.matchValue) return false;
	if (rule.matchType === 'regex' && !isSafeRegex(rule.matchValue)) {
		return false;
	}
	if (typeof rule.route !== 'string' || !rule.route.startsWith('/')) {
		return false;
	}
	if (typeof rule.filterField !== 'string' || !rule.filterField.trim()) {
		return false;
	}
	if (
		rule.filterOperator !== undefined &&
		!FILTER_OPERATORS.includes(rule.filterOperator as string)
	) {
		return false;
	}
	return true;
};

export const loadCustomSmartSearchRules = (): SmartSearchRule[] => {
	try {
		const stored = getConfigStorageItem(SMART_SEARCH_CUSTOM_RULES_KEY);
		if (!Array.isArray(stored)) return [];
		return stored
			.filter(isValidSmartSearchRule)
			.map((rule) => ({ ...rule, isBuiltIn: false }));
	} catch {
		return [];
	}
};

export const saveCustomSmartSearchRules = (
	rules: SmartSearchRule[],
): SmartSearchRule[] => {
	const safeRules = rules
		.filter(isValidSmartSearchRule)
		.map((rule) => ({ ...rule, isBuiltIn: false }));
	try {
		setConfigStorageItem({
			key: SMART_SEARCH_CUSTOM_RULES_KEY,
			value: safeRules,
		});
	} catch {
		// localStorage unavailable (private mode, quota) — keep working in-memory
	}
	return safeRules;
};

export const upsertCustomSmartSearchRule = (
	rule: SmartSearchRule,
): SmartSearchRule[] => {
	const current = loadCustomSmartSearchRules();
	if (!isValidSmartSearchRule(rule)) return current;
	// Custom rules may not shadow built-in ids.
	if (builtInSmartSearchRules.some((builtIn) => builtIn.id === rule.id)) {
		return current;
	}
	const next = [
		...current.filter((existing) => existing.id !== rule.id),
		{ ...rule, isBuiltIn: false },
	];
	return saveCustomSmartSearchRules(next);
};

export const removeCustomSmartSearchRule = (
	ruleId: string,
): SmartSearchRule[] => {
	const next = loadCustomSmartSearchRules().filter(
		(rule) => rule.id !== ruleId,
	);
	return saveCustomSmartSearchRules(next);
};

// ---------------------------------------------------------------------------
// Backend persistence (ClientConfiguration endpoint)
// ---------------------------------------------------------------------------

type ClientConfigLike = {
	Id?: string;
	Parameter?: string;
	Value?: string;
};

// Extract + validate the rules stored in the ClientConfiguration list.
export const parseRulesFromConfigurations = (
	configurations: ClientConfigLike[] | null | undefined,
): SmartSearchRule[] => {
	if (!Array.isArray(configurations)) return [];
	const item = configurations.find(
		(config) => config?.Parameter === SMART_SEARCH_CONFIG_PARAMETER,
	);
	if (!item?.Value) return [];
	try {
		const parsed = JSON.parse(item.Value);
		if (!Array.isArray(parsed)) return [];
		return parsed
			.filter(isValidSmartSearchRule)
			.map((rule) => ({ ...rule, isBuiltIn: false }));
	} catch {
		return [];
	}
};

// The ClientConfiguration row id for the smart search rules, if it exists yet.
export const findSmartSearchConfigId = (
	configurations: ClientConfigLike[] | null | undefined,
): string | null => {
	if (!Array.isArray(configurations)) return null;
	return (
		configurations.find(
			(config) => config?.Parameter === SMART_SEARCH_CONFIG_PARAMETER,
		)?.Id ?? null
	);
};

// Whether the backend already has a smart search config row.
export const hasSmartSearchConfig = (
	configurations: ClientConfigLike[] | null | undefined,
): boolean =>
	Array.isArray(configurations) &&
	configurations.some(
		(config) => config?.Parameter === SMART_SEARCH_CONFIG_PARAMETER,
	);

// Build the create/update payload in the same shape the column/field presets
// use (see ColModal/utils.updateConfData).
export const buildSmartSearchConfigPayload = (
	id: string,
	rules: SmartSearchRule[],
) => ({
	id,
	data: {
		Name: getCookieCustom('user_id') || -1,
		Parameter: SMART_SEARCH_CONFIG_PARAMETER,
		Type: 'string',
		Value: JSON.stringify(
			rules
				.filter(isValidSmartSearchRule)
				.map((rule) => ({ ...rule, isBuiltIn: false })),
		),
	},
});

// Mirror backend rules into the localStorage cache the matcher reads. Returns
// false (without touching the cache) when the backend has no rules row yet, so
// a transient/empty fetch never wipes local-only rules before the first save.
export const syncSmartSearchRulesFromConfigurations = (
	configurations: ClientConfigLike[] | null | undefined,
): boolean => {
	if (!hasSmartSearchConfig(configurations)) return false;
	saveCustomSmartSearchRules(parseRulesFromConfigurations(configurations));
	return true;
};

// Built-in rules win id collisions; result sorted by priority (highest first).
export const getSmartSearchRules = (): SmartSearchRule[] => {
	const builtInIds = new Set(builtInSmartSearchRules.map((rule) => rule.id));
	const customRules = loadCustomSmartSearchRules().filter(
		(rule) => !builtInIds.has(rule.id),
	);
	return [...builtInSmartSearchRules, ...customRules].sort(
		(a, b) => b.priority - a.priority,
	);
};

const ruleMatches = (rule: SmartSearchRule, text: string): boolean => {
	switch (rule.matchType) {
		case 'startsWith':
			return text.toLowerCase().startsWith(rule.matchValue.toLowerCase());
		case 'includes':
			return text.toLowerCase().includes(rule.matchValue.toLowerCase());
		case 'equals':
			return text.toLowerCase() === rule.matchValue.toLowerCase();
		case 'regex':
			try {
				return new RegExp(rule.matchValue, 'i').test(text);
			} catch {
				return false;
			}
		default:
			return false;
	}
};

// All matching rules, highest priority first.
export const matchSmartSearchRules = (
	text: string,
	rules: SmartSearchRule[] = getSmartSearchRules(),
): SmartSearchRule[] => {
	const trimmed = text.trim();
	if (!trimmed) return [];
	return rules.filter((rule) => ruleMatches(rule, trimmed));
};

export const matchSmartSearchRule = (
	text: string,
	rules: SmartSearchRule[] = getSmartSearchRules(),
): SmartSearchRule | null => matchSmartSearchRules(text, rules)[0] ?? null;

const isValidHistoryEntry = (
	value: unknown,
): value is SmartSearchHistoryEntry => {
	if (!value || typeof value !== 'object' || Array.isArray(value)) {
		return false;
	}
	const entry = value as Record<string, unknown>;
	if (typeof entry.text !== 'string' || !entry.text.trim()) return false;
	if (entry.ruleId !== null && typeof entry.ruleId !== 'string') return false;
	if (entry.route !== null && typeof entry.route !== 'string') return false;
	if (typeof entry.timestamp !== 'number') return false;
	return true;
};

export const loadRecentSmartSearches = (): SmartSearchHistoryEntry[] => {
	try {
		const stored = getConfigStorageItem(SMART_SEARCH_RECENT_KEY);
		if (!Array.isArray(stored)) return [];
		return stored
			.filter(isValidHistoryEntry)
			.slice(0, SMART_SEARCH_HISTORY_LIMIT);
	} catch {
		return [];
	}
};

export const addRecentSmartSearch = (entry: {
	text: string;
	ruleId: string | null;
	route: string | null;
}): SmartSearchHistoryEntry[] => {
	const trimmed = entry.text.trim();
	if (!trimmed) return loadRecentSmartSearches();
	const next = [
		{ ...entry, text: trimmed, timestamp: Date.now() },
		...loadRecentSmartSearches().filter((item) => item.text !== trimmed),
	].slice(0, SMART_SEARCH_HISTORY_LIMIT);
	try {
		setConfigStorageItem({ key: SMART_SEARCH_RECENT_KEY, value: next });
	} catch {
		// localStorage unavailable — history just won't persist
	}
	return next;
};

export const clearRecentSmartSearches = (): SmartSearchHistoryEntry[] => {
	try {
		removeConfigStorageItem({ key: SMART_SEARCH_RECENT_KEY });
	} catch {
		// localStorage unavailable — nothing to clear
	}
	return [];
};
